import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ContentType } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { Spinner } from "../components/Spinner";
import { ErrorBanner } from "../components/ErrorBanner";
import { BooksInProducts } from "../components/BooksInProducts";

type RebuildState =
  | { status: "idle" }
  | { status: "working" }
  | { status: "done" }
  | { status: "error"; message: string };

export function Dashboard() {
  const api = useApi();
  const load = useCallback(async () => (await api.listContentTypes()).filter(type => type.slug !== "books"), [api]);
  const { data, loading, error, reload } = useAsync<ContentType[]>(load, [load]);

  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [rebuild, setRebuild] = useState<RebuildState>({ status: "idle" });

  // Best-effort entry counts; failures resolve to null (shown as "—").
  useEffect(() => {
    if (!data) return;
    let active = true;
    void (async () => {
      const entries = await Promise.all(
        data.map(async (type): Promise<[string, number | null]> => {
          try {
            const items = await api.listEntries(type.id);
            return [type.id, items.length];
          } catch {
            return [type.id, null];
          }
        }),
      );
      if (active) setCounts(Object.fromEntries(entries));
    })();
    return () => {
      active = false;
    };
  }, [api, data]);

  const onRebuild = async () => {
    setRebuild({ status: "working" });
    try {
      await api.publishSite();
      setRebuild({ status: "done" });
    } catch (err) {
      setRebuild({ status: "error", message: err instanceof Error ? err.message : "Rebuild failed." });
    }
  };

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Dashboard</h1>
          <p className="page__subtitle">Overview of your content types.</p>
        </div>
        <div className="page__actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void onRebuild()}
            disabled={rebuild.status === "working"}
          >
            {rebuild.status === "working" ? "Rebuilding…" : "Rebuild site"}
          </button>
        </div>
      </div>

      {rebuild.status === "done" ? (
        <div className="notice notice--success" role="status">
          Site rebuild requested. Publishing runs in the background.
        </div>
      ) : null}
      {rebuild.status === "error" ? (
        <ErrorBanner title="Rebuild failed" message={rebuild.message} />
      ) : null}

      <BooksInProducts />
      <section className="notice sales-dashboard-link">
        <div><strong>Sales</strong><p>Track Stripe and Square payments, refunds and sales history.</p></div>
        <Link to="/sales" className="btn btn--secondary">View sales</Link>
      </section>
      {loading ? <Spinner label="Loading content types…" /> : null}
      {error ? <ErrorBanner message={error.message} onRetry={reload} /> : null}

      {data ? (
        data.length === 0 ? (
          <div className="empty-state">
            <p>No content types yet.</p>
            <Link to="/content-types/new" className="btn btn--primary">
              Create your first content type
            </Link>
          </div>
        ) : (
          <div className="card-grid">
            {data.map((type) => {
              const count = counts[type.id];
              return (
                <div key={type.id} className="card">
                  <div className="card__header">
                    <h2 className="card__title">{type.name}</h2>
                    <code className="card__slug">{type.slug}</code>
                  </div>
                  <div className="card__stat">
                    <span className="card__stat-value">
                      {count === undefined ? "…" : count === null ? "—" : count}
                    </span>
                    <span className="card__stat-label">entries</span>
                  </div>
                  <div className="card__footer">
                    <Link to={`/content-types/${type.id}/entries`} className="btn btn--secondary btn--sm">
                      Entries
                    </Link>
                    <Link to={`/content-types/${type.id}/edit`} className="btn btn--ghost btn--sm">
                      Edit schema
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : null}
    </div>
  );
}
